import React from 'react'
import {
  Text,
  Flex,
  Box,
  Heading,
  Link,
  Icon,
  Avatar,
  useToast,
  useTheme
} from '@homebotapp/hb-react-component-catalog'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/browser'
import { Link as ReactRouterLink } from 'react-router-dom'
import { defineMessages, useIntl } from 'react-intl'
import { PATH_HELPERS } from '../../../constants/navigation'
import { selectClientId } from '../../../store/selectors/client'
import { formatAbbrCurrency, formatNumber } from '../../../helpers/formatters'
import { interpolateColor } from '../helpers/marketTempHelpers'
import { selectCustomerProfile } from '../../../store/selectors/customerProfile'
import { DmModal } from '../../../Components/shared/DmModal/DmModal'
import { useSendMessage } from '../../../hooks/useSendMessage'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'
import useViewport from '../../../hooks/useViewport'
import { useGetMarketData } from '../hooks/useGetMarketData'
import { GeoAreaLevel } from '../../../api/gqlaxy/gql/generated/graphql'
import { hasCustomerToken } from '../../../auth'

interface MarketTemperatureIndicatorProps {
  zipCode: string
  onSendMessageComplete?: () => void
}

export const MSG = defineMessages({
  mapView: {
    id: 'MarketTemperature.mapView',
    defaultMessage: 'Map view'
  },
  marketOverview: {
    id: 'MarketTemperature.marketOverview',
    defaultMessage: 'Market Overview'
  },
  buyersMarket: {
    id: 'MarketTemperature.buyersMarket',
    defaultMessage: "buyer's market"
  },
  buyersMarketDescription: {
    id: 'MarketTemperature.buyersMarketDescription',
    defaultMessage: 'Better deals and less competition'
  },
  sellersMarket: {
    id: 'MarketTemperature.sellersMarket',
    defaultMessage: "seller's market"
  },
  sellersMarketDescription: {
    id: 'MarketTemperature.sellersMarketDescription',
    defaultMessage: 'Rising values but more competition'
  },
  averagePrice: {
    id: 'MarketTemperature.averagePrice',
    defaultMessage: 'Average price'
  },
  sizeInBudget: {
    id: 'MarketTemperature.sizeInBudget',
    defaultMessage: 'Size in your budget'
  },
  buyingTimeline: {
    id: 'MarketTemperature.buyingTimeline',
    defaultMessage: 'Buying Timeline'
  },
  askYourAgent: {
    id: 'MarketTemperature.askYourAgent',
    defaultMessage: 'Ask {agentName} a question'
  },
  howIsTheMarket: {
    id: 'MarketTemperature.howIsTheMarket',
    defaultMessage: 'How is the market in {zipCode}?'
  },
  messageSent: {
    id: 'MarketTemperature.messageSent',
    defaultMessage: 'Message sent successfully!'
  },
  messageNotSent: {
    id: 'MarketTemperature.messageNotSent',
    defaultMessage: 'Message could not be sent.'
  }
})

const MARKET_TEMP_MIN = 7
const MARKET_TEMP_MAX = 91

export const MarketTemperatureIndicatorV2: React.FC<MarketTemperatureIndicatorProps> = ({
  zipCode,
  onSendMessageComplete
}) => {
  const viewport = useViewport()
  const isImpersonating = hasCustomerToken()
  const intl = useIntl()
  const toast = useToast()
  const clientId = useSelector(selectClientId)
  const [showModal, setShowModal] = React.useState(false)

  const { mutateAsync: sendMessage } = useSendMessage()

  const { data: marketData } = useGetMarketData(
    {
      input: {
        geoArea: {
          level: GeoAreaLevel.PostalCode,
          zipCode,
          name: '',
          stateAbbreviation: ''
        },
        clientId: clientId as string
      }
    },
    {
      enabled: !!zipCode && !!clientId
    }
  )

  const { marketData: data } = marketData || {}

  const { theme } = useTheme()

  const indicatorColor = interpolateColor(13, theme)

  const customerProfile = useSelector(selectCustomerProfile) || {}

  const askYourAgent = async () => {
    const result = await sendMessage({
      input: {
        clientId: clientId as string,
        topicKey: CUSTOMER_MESSAGES.HOW_IS_MARKET.topicKey,
        topic: CUSTOMER_MESSAGES.HOW_IS_MARKET.topic,
        message: intl.formatMessage(CUSTOMER_MESSAGES.HOW_IS_MARKET.message, {
          zipCode
        })
      }
    })
    if (result.sendMessage) {
      setShowModal(false)
      onSendMessageComplete?.()
      toast({
        title: intl.formatMessage(MSG.messageSent),
        status: 'success'
      })
    } else {
      toast({
        title: intl.formatMessage(MSG.messageNotSent),
        status: 'error'
      })
    }
  }

  return (
    <>
      <Box
        width='100%'
        maxWidth='800px'
        margin='0 auto'
        mt={8}
        bgColor='neutral.200'
        border='1px solid'
        borderColor='neutral.300'
        borderRadius='16px'
        mb={12}
      >
        <Flex direction='column' py={4} px={viewport.isSmall ? 0 : 6}>
          <Flex mb={4} px={!viewport.isSmall ? 0 : 4} justifyContent='space-between' alignItems='center'>
            <Text size='lg' fontWeight='bold' color='neutral.900' mb={0}>
              {intl.formatMessage(MSG.marketOverview)}
            </Text>
            <Link
              display='flex'
              alignItems='center'
              fontWeight='semibold'
              as={ReactRouterLink}
              to={
                zipCode
                  ? PATH_HELPERS.zip.buildPath(clientId, zipCode)
                  : PATH_HELPERS.markets.buildPath(clientId, '/explore/map')
              }
              size='m'
            >
              <Icon mr={2} name='map-marked-alt-filled' />
              {intl.formatMessage(MSG.mapView)}
            </Link>
          </Flex>

          <Box padding={4}>
            <Box
              position='relative'
              height='24px'
              borderRadius='12px'
              sx={{
                background: `linear-gradient(90deg, ${theme.isLightThemeType ? '#105998' : '#5AA3EE'} 0%, \
                  ${theme.isLightThemeType ? '#5D00C5' : '#BDA8FF'} 50%, \
                  ${theme.isLightThemeType ? '#AD0054' : '#F7749E'} 100%)`
              }}
            >
              <Flex position='absolute' width='100%' height='100%' justify='space-between' align='center' paddingX={3}>
                <Box>
                  {data?.marketTemp >= MARKET_TEMP_MIN && (
                    <Text size='s' color='neutral.100' mb={0} fontWeight='600'>
                      0
                    </Text>
                  )}
                </Box>
                <Box>
                  {data?.marketTemp <= MARKET_TEMP_MAX && (
                    <Text size='s' color='neutral.100' mb={0} fontWeight='600'>
                      100
                    </Text>
                  )}
                </Box>
              </Flex>
              <Box
                position='absolute'
                left={`${data?.marketTemp}%`}
                top='50%'
                transform='translate(-50%, -50%)'
                width='56px'
                height='56px'
                borderRadius='50%'
                backgroundColor='neutral.50'
                color='neutral.900'
                border='2.5px solid'
                borderColor={indicatorColor}
                display='flex'
                alignItems='center'
                justifyContent='center'
                sx={{
                  fontSize: '30px'
                }}
              >
                {data?.marketTemp}
              </Box>
            </Box>
            <Flex justify='space-between' marginTop={8} mb={8}>
              <Flex flexDirection='column' flex={1} textAlign='left'>
                <Heading variant='eyebrow' size='xs' fontWeight='semibold' color='primary.700' mb={1} textAlign='left'>
                  {intl.formatMessage(MSG.buyersMarket)}
                </Heading>
                <Text size='2xs' color='neutral.700' whiteSpace='wrap'>
                  {intl.formatMessage(MSG.buyersMarketDescription)}
                </Text>
              </Flex>
              <Flex flexDirection='column' textAlign='right' flex={1}>
                <Heading variant='eyebrow' size='xs' fontWeight='semibold' color='brand.700' mb={1} textAlign='right'>
                  {intl.formatMessage(MSG.sellersMarket)}
                </Heading>
                <Text size='2xs' color='neutral.700' whiteSpace='wrap'>
                  {intl.formatMessage(MSG.sellersMarketDescription)}
                </Text>
              </Flex>
            </Flex>
            <Flex
              justifyContent='center'
              gap={viewport.isSmall ? '12px' : 10}
              flexDirection={viewport.isSmall ? 'column' : 'row'}
            >
              {data?.avgPrice && (
                <StatBox
                  icon='💵'
                  label={intl.formatMessage(MSG.averagePrice)}
                  value={formatAbbrCurrency(data?.avgPrice)}
                />
              )}
              {data?.sqftAfford && (
                <StatBox
                  icon='🏠'
                  label={intl.formatMessage(MSG.sizeInBudget)}
                  value={`${formatNumber(data?.sqftAfford)} SQFT`}
                />
              )}
              {data?.buyingTimeline && (
                <StatBox
                  icon='🚛'
                  label={intl.formatMessage(MSG.buyingTimeline)}
                  value={`${data?.buyingTimeline} days`}
                />
              )}
            </Flex>
          </Box>
        </Flex>
        <Flex
          cursor='pointer'
          onClick={() => {
            if (!isImpersonating) {
              setShowModal(true)
            }
          }}
          height='57px'
          bgColor='whiteAlpha.100'
          justifyContent='center'
          alignItems='center'
        >
          <Avatar
            size='sm'
            src={customerProfile.photoUri}
            style={{
              border: '2px solid #fff',
              boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Text size='sm' fontWeight='bold' color='neutral.900' textAlign='center' mt={2} ml={2}>
            {intl.formatMessage(MSG.askYourAgent, {
              agentName: `${customerProfile.firstName} ${customerProfile.lastName}`
            })}
          </Text>
        </Flex>
      </Box>
      <DmModal
        onClose={() => {
          setShowModal(false)
        }}
        isOpen={showModal}
        defaultMessage={intl.formatMessage(MSG.howIsTheMarket, {
          zipCode
        })}
        avatarSrc={customerProfile.photoUri}
        name={`${customerProfile.firstName} ${customerProfile.lastName}`}
        onSubmit={askYourAgent}
      />
    </>
  )
}

interface StatBoxProps {
  icon: string
  label: string
  value: string
}

const StatBox = ({ icon, label, value }: StatBoxProps) => {
  const viewport = useViewport()
  return (
    <Flex
      flexDirection={viewport.isSmall ? 'row' : 'column'}
      justifyContent={viewport.isSmall ? 'space-between' : undefined}
      alignItems='center'
      textAlign='center'
    >
      <Heading variant='eyebrow' size='2xs' color='neutral.900' mb={viewport.isSmall ? 0 : 1}>
        {icon} {label}
      </Heading>
      <Text fontWeight='bold' color='neutral.900' size={viewport.isSmall ? 'sm' : 'lg'} m={viewport.isSmall ? 0 : 1}>
        {value}
      </Text>
    </Flex>
  )
}
